import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type SupplierCommunicationChannel =
  | 'any'
  | 'email'
  | 'whatsapp'
  | 'sms'
  | 'phone'
  | string;
export type SupplierStatus = 'active' | 'inactive' | string;
export type PurchaseOrderStatus =
  | 'draft'
  | 'sent'
  | 'partially_fulfilled'
  | 'fulfilled'
  | 'cancelled'
  | string;

export interface BackofficeSupplier {
  _id: string;
  bp_supplier_name: string;
  bp_supplier_contact_person_name?: string;
  bp_supplier_phone_number?: string;
  bp_supplier_whatsapp_number?: string;
  bp_supplier_email_address?: string;
  bp_supplier_physical_address?: string;
  bp_supplier_tax_vat_number?: string;
  bp_supplier_preferred_communication_channel?: SupplierCommunicationChannel;
  bp_supplier_status?: SupplierStatus;
  bp_supplier_notes?: string;
  bp_supplier_created_at?: string;
  bp_supplier_updated_at?: string;
}

export interface PurchaseOrderLineItem {
  _id?: string;
  bp_line_item_product_id?: string | Record<string, unknown>;
  bp_line_item_variant_id?: string;
  bp_line_item_variant_attributes?: Record<string, string>;
  bp_line_item_product_name?: string;
  bp_line_item_quantity?: number;
  bp_line_item_mass?: number;
  bp_line_item_received_quantity?: number;
  bp_line_item_received?: number;
  bp_line_item_unit_price?: number;
  bp_line_item_product_price_type?: string;
  bp_line_item_unit_of_mass?: string;
  bp_line_item_product_category?: string;
  bp_line_item_product_sub_category?: string;
  bp_line_item_product_barcode?: string;
  bp_line_item_product_mass?: string | number;
  bp_line_item_product_size?: string;
  bp_line_item_product_model?: string;
  bp_line_item_notes?: string;
}

export interface BackofficePurchaseOrder {
  _id: string;
  bp_purchase_order_number?: string;
  bp_purchase_order_supplier_id?: string | BackofficeSupplier;
  bp_purchase_order_location_id?:
    | string
    | { _id?: string; bp_location_name?: string };
  bp_purchase_order_status?: PurchaseOrderStatus;
  bp_purchase_order_created_at?: string;
  createdAt?: string;
  bp_purchase_order_updated_at?: string;
  updatedAt?: string;
  bp_purchase_order_expected_delivery_date?: string;
  bp_purchase_order_total?: number;
  total?: number;
  bp_purchase_order_notes?: string;
  bp_purchase_order_line_items?: PurchaseOrderLineItem[];
  lineItems?: PurchaseOrderLineItem[];
  bp_purchase_order_sent_via?: string[];
  bp_purchase_order_sent_at?: string | null;
}

export interface BackofficeSupplierInvoice {
  _id: string;
  bp_supplier_invoice_number?: string;
  bp_supplier_invoice_date?: string;
  bp_supplier_invoice_due_date?: string;
  bp_supplier_invoice_total_amount?: number;
  bp_supplier_invoice_tax_amount?: number;
  bp_supplier_invoice_notes?: string;
  bp_supplier_invoice_file?: string;
  bp_supplier_invoice_supplier_id?: string | BackofficeSupplier;
  bp_supplier_invoice_purchase_order_id?: string | BackofficePurchaseOrder;
  bp_supplier_invoice_created_at?: string;
}

export interface BackofficeGoodsReceiptLine {
  id?: string;
  _id?: string;
  bp_gr_line_po_line_id?: string;
  bp_gr_line_variant_id?: string;
  bp_gr_line_product_name?: string;
  bp_gr_line_quantity_received?: number | string;
  bp_gr_line_price_type?: string;
  bp_gr_line_unit_of_mass?: string;
}

export interface BackofficeGoodsReceipt {
  _id: string;
  bp_goods_receipt_number?: string;
  bp_goods_receipt_date?: string;
  bp_goods_receipt_created_at?: string;
  bp_goods_receipt_notes?: string;
  bp_goods_receipt_line_items?: BackofficeGoodsReceiptLine[];
  bp_purchase_order_id?:
    | string
    | {
      _id?: string;
      bp_purchase_order_number?: string;
    };
  bp_supplier_id?:
    | string
    | {
      _id?: string;
      bp_supplier_name?: string;
    };
  createdAt?: string;
  notes?: string;
}

type EntityState<T> = {
  data: T;
  loading: boolean;
  error: string | null;
};

type ByIdState<T> = Record<string, EntityState<T>>;

const createEntityState = <T>(defaultData: T): EntityState<T> => ({
  data: defaultData,
  loading: false,
  error: null,
});

export interface SuppliersListState {
  items: BackofficeSupplier[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  searchTerm: string;
  hasNextPage: boolean;
}

export interface BackofficeSuppliersState {
  list: SuppliersListState;
  supplierById: ByIdState<BackofficeSupplier | null>;
  purchaseOrdersBySupplierId: ByIdState<BackofficePurchaseOrder[]>;
  purchaseOrderById: ByIdState<BackofficePurchaseOrder | null>;
  invoicesBySupplierId: ByIdState<BackofficeSupplierInvoice[]>;
  invoicesByPOId: ByIdState<BackofficeSupplierInvoice[]>;
  goodsReceiptsByPOId: ByIdState<BackofficeGoodsReceipt[]>;
}

const initialListState: SuppliersListState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 50,
  loading: false,
  error: null,
  searchTerm: '',
  hasNextPage: true,
};

const initialState: BackofficeSuppliersState = {
  list: initialListState,
  supplierById: {},
  purchaseOrdersBySupplierId: {},
  purchaseOrderById: {},
  invoicesBySupplierId: {},
  invoicesByPOId: {},
  goodsReceiptsByPOId: {},
};

const ensureSupplierById = (
  state: BackofficeSuppliersState,
  id: string,
): EntityState<BackofficeSupplier | null> => {
  if (!state.supplierById[id]) {
    state.supplierById[id] = createEntityState<BackofficeSupplier | null>(null);
  }
  return state.supplierById[id];
};

const ensurePurchaseOrdersBySupplierId = (
  state: BackofficeSuppliersState,
  id: string,
): EntityState<BackofficePurchaseOrder[]> => {
  if (!state.purchaseOrdersBySupplierId[id]) {
    state.purchaseOrdersBySupplierId[id] = createEntityState<
      BackofficePurchaseOrder[]
    >([]);
  }
  return state.purchaseOrdersBySupplierId[id];
};

const ensurePurchaseOrderById = (
  state: BackofficeSuppliersState,
  id: string,
): EntityState<BackofficePurchaseOrder | null> => {
  if (!state.purchaseOrderById[id]) {
    state.purchaseOrderById[id] =
      createEntityState<BackofficePurchaseOrder | null>(null);
  }
  return state.purchaseOrderById[id];
};

const ensureInvoicesBySupplierId = (
  state: BackofficeSuppliersState,
  id: string,
): EntityState<BackofficeSupplierInvoice[]> => {
  if (!state.invoicesBySupplierId[id]) {
    state.invoicesBySupplierId[id] = createEntityState<
      BackofficeSupplierInvoice[]
    >([]);
  }
  return state.invoicesBySupplierId[id];
};

const ensureInvoicesByPOId = (
  state: BackofficeSuppliersState,
  id: string,
): EntityState<BackofficeSupplierInvoice[]> => {
  if (!state.invoicesByPOId[id]) {
    state.invoicesByPOId[id] = createEntityState<BackofficeSupplierInvoice[]>(
      [],
    );
  }
  return state.invoicesByPOId[id];
};

const ensureGoodsReceiptsByPOId = (
  state: BackofficeSuppliersState,
  id: string,
): EntityState<BackofficeGoodsReceipt[]> => {
  if (!state.goodsReceiptsByPOId[id]) {
    state.goodsReceiptsByPOId[id] = createEntityState<BackofficeGoodsReceipt[]>(
      [],
    );
  }
  return state.goodsReceiptsByPOId[id];
};

const suppliersSlice = createSlice({
  name: 'backofficeSuppliers',
  initialState,
  reducers: {
    setSuppliersSearchTerm: (state, action: PayloadAction<string>) => {
      state.list.searchTerm = action.payload;
    },
    setSuppliersListLoading: (state, action: PayloadAction<boolean>) => {
      state.list.loading = action.payload;
      if (action.payload) {
        state.list.error = null;
      }
    },
    setSuppliersListError: (state, action: PayloadAction<string | null>) => {
      state.list.error = action.payload;
      state.list.loading = false;
    },
    setSuppliersListPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (typeof action.payload.page === 'number')
        state.list.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.list.pageSize = action.payload.pageSize;
    },
    setSuppliersListData: (
      state,
      action: PayloadAction<{
        suppliers: BackofficeSupplier[];
        total: number;
        page: number;
        limit: number;
      }>,
    ) => {
      const suppliers = Array.isArray(action.payload.suppliers)
        ? action.payload.suppliers
        : [];
      state.list.items = suppliers;
      state.list.total = Number(action.payload.total || suppliers.length);
      state.list.page = Number(action.payload.page || 1);
      state.list.pageSize = Number(action.payload.limit || state.list.pageSize);
      state.list.hasNextPage = state.list.items.length < state.list.total;
      state.list.loading = false;
      state.list.error = null;
    },
    appendSuppliersListData: (
      state,
      action: PayloadAction<{
        suppliers: BackofficeSupplier[];
        total: number;
        page: number;
        limit: number;
      }>,
    ) => {
      const incoming = Array.isArray(action.payload.suppliers)
        ? action.payload.suppliers
        : [];
      const ids = new Set(state.list.items.map(item => item._id));
      incoming.forEach(item => {
        if (!ids.has(item._id)) {
          state.list.items.push(item);
          ids.add(item._id);
        }
      });
      state.list.total = Number(action.payload.total || state.list.total);
      state.list.page = Number(action.payload.page || state.list.page);
      state.list.pageSize = Number(action.payload.limit || state.list.pageSize);
      state.list.hasNextPage = state.list.items.length < state.list.total;
      state.list.loading = false;
      state.list.error = null;
    },
    upsertSupplierInList: (
      state,
      action: PayloadAction<BackofficeSupplier>,
    ) => {
      const supplier = action.payload;
      if (!supplier || !supplier._id) return;
      const idx = state.list.items.findIndex(item => item._id === supplier._id);
      if (idx >= 0) {
        state.list.items[idx] = { ...state.list.items[idx], ...supplier };
      } else {
        state.list.items.unshift(supplier);
      }
    },
    setSupplierByIdLoading: (state, action: PayloadAction<string>) => {
      const entity = ensureSupplierById(state, action.payload);
      entity.loading = true;
      entity.error = null;
    },
    setSupplierByIdData: (
      state,
      action: PayloadAction<{ id: string; data: BackofficeSupplier | null }>,
    ) => {
      const entity = ensureSupplierById(state, action.payload.id);
      entity.data = action.payload.data;
      entity.loading = false;
      entity.error = null;
    },
    setSupplierByIdError: (
      state,
      action: PayloadAction<{ id: string; error: string | null }>,
    ) => {
      const entity = ensureSupplierById(state, action.payload.id);
      entity.loading = false;
      entity.error = action.payload.error;
    },
    setPurchaseOrdersBySupplierIdLoading: (
      state,
      action: PayloadAction<string>,
    ) => {
      const entity = ensurePurchaseOrdersBySupplierId(state, action.payload);
      entity.loading = true;
      entity.error = null;
    },
    setPurchaseOrdersBySupplierIdData: (
      state,
      action: PayloadAction<{ id: string; data: BackofficePurchaseOrder[] }>,
    ) => {
      const entity = ensurePurchaseOrdersBySupplierId(state, action.payload.id);
      entity.data = Array.isArray(action.payload.data)
        ? action.payload.data
        : [];
      entity.loading = false;
      entity.error = null;
    },
    setPurchaseOrdersBySupplierIdError: (
      state,
      action: PayloadAction<{ id: string; error: string | null }>,
    ) => {
      const entity = ensurePurchaseOrdersBySupplierId(state, action.payload.id);
      entity.loading = false;
      entity.error = action.payload.error;
    },
    upsertPurchaseOrderBySupplierId: (
      state,
      action: PayloadAction<{
        supplierId: string;
        order: BackofficePurchaseOrder;
      }>,
    ) => {
      const { supplierId, order } = action.payload;
      if (!supplierId || !order?._id) return;
      const entity = ensurePurchaseOrdersBySupplierId(state, supplierId);
      const idx = entity.data.findIndex(item => item._id === order._id);
      if (idx >= 0) {
        entity.data[idx] = { ...entity.data[idx], ...order };
      } else {
        entity.data.unshift(order);
      }
    },
    setPurchaseOrderByIdLoading: (state, action: PayloadAction<string>) => {
      const entity = ensurePurchaseOrderById(state, action.payload);
      entity.loading = true;
      entity.error = null;
    },
    setPurchaseOrderByIdData: (
      state,
      action: PayloadAction<{
        id: string;
        data: BackofficePurchaseOrder | null;
      }>,
    ) => {
      const entity = ensurePurchaseOrderById(state, action.payload.id);
      entity.data = action.payload.data;
      entity.loading = false;
      entity.error = null;
    },
    setPurchaseOrderByIdError: (
      state,
      action: PayloadAction<{ id: string; error: string | null }>,
    ) => {
      const entity = ensurePurchaseOrderById(state, action.payload.id);
      entity.loading = false;
      entity.error = action.payload.error;
    },
    setInvoicesBySupplierIdLoading: (state, action: PayloadAction<string>) => {
      const entity = ensureInvoicesBySupplierId(state, action.payload);
      entity.loading = true;
      entity.error = null;
    },
    setInvoicesBySupplierIdData: (
      state,
      action: PayloadAction<{ id: string; data: BackofficeSupplierInvoice[] }>,
    ) => {
      const entity = ensureInvoicesBySupplierId(state, action.payload.id);
      entity.data = Array.isArray(action.payload.data)
        ? action.payload.data
        : [];
      entity.loading = false;
      entity.error = null;
    },
    setInvoicesBySupplierIdError: (
      state,
      action: PayloadAction<{ id: string; error: string | null }>,
    ) => {
      const entity = ensureInvoicesBySupplierId(state, action.payload.id);
      entity.loading = false;
      entity.error = action.payload.error;
    },
    setInvoicesByPOIdLoading: (state, action: PayloadAction<string>) => {
      const entity = ensureInvoicesByPOId(state, action.payload);
      entity.loading = true;
      entity.error = null;
    },
    setInvoicesByPOIdData: (
      state,
      action: PayloadAction<{ id: string; data: BackofficeSupplierInvoice[] }>,
    ) => {
      const entity = ensureInvoicesByPOId(state, action.payload.id);
      entity.data = Array.isArray(action.payload.data)
        ? action.payload.data
        : [];
      entity.loading = false;
      entity.error = null;
    },
    setInvoicesByPOIdError: (
      state,
      action: PayloadAction<{ id: string; error: string | null }>,
    ) => {
      const entity = ensureInvoicesByPOId(state, action.payload.id);
      entity.loading = false;
      entity.error = action.payload.error;
    },
    setGoodsReceiptsByPOIdLoading: (state, action: PayloadAction<string>) => {
      const entity = ensureGoodsReceiptsByPOId(state, action.payload);
      entity.loading = true;
      entity.error = null;
    },
    setGoodsReceiptsByPOIdData: (
      state,
      action: PayloadAction<{ id: string; data: BackofficeGoodsReceipt[] }>,
    ) => {
      const entity = ensureGoodsReceiptsByPOId(state, action.payload.id);
      entity.data = Array.isArray(action.payload.data)
        ? action.payload.data
        : [];
      entity.loading = false;
      entity.error = null;
    },
    setGoodsReceiptsByPOIdError: (
      state,
      action: PayloadAction<{ id: string; error: string | null }>,
    ) => {
      const entity = ensureGoodsReceiptsByPOId(state, action.payload.id);
      entity.loading = false;
      entity.error = action.payload.error;
    },
    clearSuppliersState: () => initialState,
  },
});

export const {
  setSuppliersSearchTerm,
  setSuppliersListLoading,
  setSuppliersListError,
  setSuppliersListPagination,
  setSuppliersListData,
  appendSuppliersListData,
  upsertSupplierInList,
  setSupplierByIdLoading,
  setSupplierByIdData,
  setSupplierByIdError,
  setPurchaseOrdersBySupplierIdLoading,
  setPurchaseOrdersBySupplierIdData,
  setPurchaseOrdersBySupplierIdError,
  upsertPurchaseOrderBySupplierId,
  setPurchaseOrderByIdLoading,
  setPurchaseOrderByIdData,
  setPurchaseOrderByIdError,
  setInvoicesBySupplierIdLoading,
  setInvoicesBySupplierIdData,
  setInvoicesBySupplierIdError,
  setInvoicesByPOIdLoading,
  setInvoicesByPOIdData,
  setInvoicesByPOIdError,
  setGoodsReceiptsByPOIdLoading,
  setGoodsReceiptsByPOIdData,
  setGoodsReceiptsByPOIdError,
  clearSuppliersState,
} = suppliersSlice.actions;

export default suppliersSlice.reducer;
